import type { Tool } from '../../types/tools'

export const PIPELINE_TOOLS: Tool[] = [
  {
    slug: 'deal-sourcing',
    name: 'Deal Sourcing',
    icon: 'radar',
    categories: ['find'],
    featured: false,
    cardDescription:
      'Find and organize companies that match your fund’s sector, stage, geography and criteria.',
    seo: {
      title: 'AI Deal Sourcing Software for Venture Capital | DiligenceGPT™',
      description:
        'Source startups that match your investment thesis. DiligenceGPT™ helps venture teams identify and organize companies by sector, stage, geography and fund-specific criteria.',
      keywords: ['AI deal sourcing', 'venture capital deal sourcing software', 'startup discovery tool', 'investment thesis startup search'],
    },
    hero: {
      supporting:
        'Identify and organize companies matching your fund’s sector, stage, geography, and investment criteria.',
      bullets: [
        'Search for companies by your thesis, not keywords alone',
        'Look beyond the deals already in your inbox',
        'Keep sourced companies organized for review',
      ],
    },
    whatIs: {
      question: 'What is AI deal sourcing?',
      paragraphs: [
        'AI deal sourcing helps venture teams find startups that match their investment thesis, rather than relying only on inbound pitches and personal networks.',
        'DiligenceGPT™’s Deal Sourcing is for investors and platform teams building pipeline. You define your fund’s criteria — sectors, stage, geography and other thesis requirements. It identifies companies that appear to match, including companies from StartupFuel’s ecosystem through Deal Radar, explains why each surfaced, and organizes results so your team can decide which to pursue.',
        'A match reflects how a company’s available information lines up with your criteria; it is not an endorsement or an assessment of investment quality. Sourced companies still go through your normal screening and diligence.',
      ],
    },
    steps: [
      {
        title: 'Define your criteria',
        body: 'Set the sectors, stage, geography and other thesis requirements you want to source against.',
      },
      {
        title: 'Run the search',
        body: 'DiligenceGPT™ identifies companies that appear to match and explains the basis for each match.',
      },
      {
        title: 'Review and organize',
        body: 'Shortlist companies worth pursuing, refine your criteria and move promising companies into screening.',
      },
    ],
    outputs: [
      { title: 'Thesis-matched company list', body: 'Companies that appear to fit your criteria, in one organized view.' },
      { title: 'Match rationale', body: 'Why each company surfaced, tied to specific criteria.' },
      { title: 'Company snapshots', body: 'Short summaries of sector, stage and location from available information.' },
      { title: 'Criteria refinement', body: 'Visibility into which criteria are narrowing or widening results.' },
      { title: 'Pipeline-ready shortlist', body: 'Companies ready to move into screening and diligence.' },
    ],
    preview: {
      kind: 'Sourcing results',
      title: 'Companies matching your thesis',
      subject: 'Sample thesis · B2B fintech · Seed–Series A · North America',
      rows: [
        { label: 'Corvane (fictional) · Seed', status: 'strong', detail: 'Treasury tooling for mid-market CFOs; matches sector, stage and region.' },
        { label: 'Billcraft (fictional) · Series A', status: 'strong', detail: 'Vendor payments automation; matches sector and stage.' },
        { label: 'Quillpay (fictional) · Seed', status: 'partial', detail: 'Matches sector; headquarters outside target region.' },
        { label: 'Fennic (fictional) · Pre-seed', status: 'partial', detail: 'Matches sector and region; earlier than target stage.' },
      ],
      gaps: ['Revenue data not available for two companies', 'Stage inferred from public information for one company'],
    },
    process: {
      heading: 'Source against the mandate you already have',
      body: 'Use the same thesis criteria for sourcing that you use for screening, so companies you find are evaluated consistently from the first touch. Move strong matches straight into a first screen.',
      configurable: ['Thesis sectors and themes', 'Stage and geography', 'Exclusions and must-haves'],
      next: { slug: 'pitch-deck-screener', prompt: 'Found a promising company? Screen its pitch deck next.' },
    },
    faqs: [
      { question: 'What does Deal Sourcing do?', answer: 'It identifies and organizes companies that appear to match your fund’s criteria and explains why each company surfaced.' },
      { question: 'What information do I need?', answer: 'Your fund’s sourcing criteria — sectors, stage, geography and any other requirements or exclusions.' },
      { question: 'Where do companies come from?', answer: 'Results can include companies from StartupFuel’s global ecosystem through Deal Radar, alongside other available information. Coverage varies by sector and region.' },
      { question: 'How should analysts review the results?', answer: 'Treat matches as leads, not recommendations. Check each company’s information, refine your criteria, and run promising companies through your normal screening process.' },
      { question: 'How does it fit into the diligence workflow?', answer: 'Sourcing is the top of the funnel. Shortlisted companies move into pitch deck screening, research and diligence.' },
    ],
    finalCta: {
      heading: 'Find the companies that haven’t reached your inbox.',
      body: 'Source against your own thesis and keep your pipeline organized from the first touch.',
    },
    related: ['pitch-deck-screener', 'market-research', 'competitor-analysis', 'founder-research', 'investment-memo-writer', 'data-room-review', 'financial-analysis', 'valuation-comps', 'portfolio-reporting'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 360,
  },
  {
    slug: 'portfolio-reporting',
    name: 'Portfolio Reporting',
    icon: 'bar-chart',
    categories: ['monitor'],
    featured: false,
    cardDescription:
      'Turn portfolio company updates into structured KPI summaries, insights and reporting drafts.',
    seo: {
      title: 'AI VC Portfolio Reporting & Monitoring | DiligenceGPT™',
      description:
        'Turn startup updates into structured KPI summaries and reporting drafts. DiligenceGPT™ helps venture teams monitor portfolio companies and prepare investor reporting.',
      keywords: ['AI VC portfolio reporting', 'venture capital portfolio monitoring', 'automated investor reporting', 'startup KPI reporting'],
    },
    hero: {
      supporting:
        'Turn company updates into structured KPI summaries, portfolio insights, and reporting drafts.',
      bullets: [
        'Standardize KPIs across company updates',
        'Spot changes that need attention',
        'Start reporting from a structured draft',
      ],
    },
    whatIs: {
      question: 'What is AI VC portfolio reporting?',
      paragraphs: [
        'AI portfolio reporting helps venture teams turn the updates their portfolio companies send — monthly emails, board decks, KPI sheets — into consistent summaries they can monitor and report on.',
        'DiligenceGPT™’s Portfolio Reporting is for partners, platform and operations teams managing a portfolio. You provide company updates and the KPIs your fund tracks. It extracts and organizes those KPIs, highlights notable changes across the portfolio, and produces reporting drafts your team can edit for internal reviews and LP reporting.',
        'Summaries reflect what companies report. Figures should be confirmed with companies before being used in external reporting.',
      ],
    },
    steps: [
      {
        title: 'Provide updates and your KPIs',
        body: 'Add portfolio company updates and the KPIs and reporting structure your fund uses.',
      },
      {
        title: 'Generate summaries',
        body: 'DiligenceGPT™ extracts KPIs, organizes them consistently and highlights notable changes.',
      },
      {
        title: 'Review and refine',
        body: 'Confirm figures, add context from your team and finalize reporting drafts.',
      },
    ],
    outputs: [
      { title: 'Structured KPI summaries', body: 'Each company’s reported metrics in a consistent format.' },
      { title: 'Notable changes', body: 'Movements in key metrics that may need follow-up.' },
      { title: 'Portfolio overview', body: 'A cross-portfolio view of reported performance.' },
      { title: 'Missing-update tracking', body: 'Companies or metrics with no recent reported data.' },
      { title: 'Reporting drafts', body: 'Draft narrative for internal reviews and LP reporting.' },
    ],
    preview: {
      kind: 'Portfolio report',
      title: 'Quarterly KPI summary',
      subject: 'Sample portfolio · 4 fictional companies',
      table: {
        caption: 'Sample KPIs — fictional companies',
        columns: ['Company', 'ARR', 'QoQ', 'Runway', 'Update'],
        rows: [
          ['Tallyfox', '$3.1M', '+12%', '14 mo', 'Received'],
          ['Corvane', '$1.4M', '+6%', '19 mo', 'Received'],
          ['Hexwell', '$0.8M', '−3%', '9 mo', 'Received'],
          ['Brindle', '—', '—', '—', 'Missing'],
        ],
      },
      gaps: ['Brindle update outstanding', 'Hexwell runway below sample 12-month threshold'],
    },
    process: {
      heading: 'Report in the format your partners and LPs expect',
      body: 'Track the KPIs your fund cares about in the structure you report on, so every company update lands in the same shape. Use financial analysis for deeper dives when a metric moves.',
      configurable: ['Tracked KPIs by company type', 'Reporting templates', 'Alert thresholds'],
      next: { slug: 'financial-analysis', prompt: 'Spotted a change? Dig in with financial analysis.' },
    },
    faqs: [
      { question: 'What does Portfolio Reporting do?', answer: 'It turns portfolio company updates into structured KPI summaries, highlights notable changes, and drafts reporting your team can edit.' },
      { question: 'What information do I need?', answer: 'The updates your portfolio companies send and the KPIs and reporting structure your fund uses.' },
      { question: 'Can I use my fund’s reporting format?', answer: 'Yes. Summaries and drafts follow the KPIs and structure your team defines.' },
      { question: 'How should teams review the results?', answer: 'Confirm extracted figures against the original updates and with companies before using them in external or LP reporting.' },
      { question: 'How does it fit into the venture workflow?', answer: 'It supports the post-investment stages — portfolio monitoring and LP reporting — and connects back to financial analysis when deeper review is needed.' },
    ],
    finalCta: {
      heading: 'Spend less time compiling updates and more time helping companies.',
      body: 'Structure portfolio KPIs and start reporting from a draft.',
    },
    related: ['financial-analysis', 'investment-memo-writer', 'data-room-review', 'valuation-comps', 'deal-sourcing', 'pitch-deck-screener', 'market-research', 'competitor-analysis', 'founder-research'],
    videoId: null,
    transcript: null,
    verifiedUsageCount: null,
    illustrativeUsageCount: 190,
  },
]
